// src/api/useDelete.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "./axiosSetup";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

const useDelete = (queryKey, token) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteMessageOpen, setDeleteMessageOpen] = useState(false);
  const [deleteMessageSeverity, setDeleteMessageSeverity] = useState("success");

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      try {
        const headers = token ? { Authorization: `Token ${token}` } : {};
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/${queryKey}/${id}/`,
          {
            headers,
          },
        );
      } catch (error) {
        throw new Error(
          error.response?.data?.detail || t("use_delete.deletfeild"),
        );
      }
    },
    onSuccess: () => {
      setDeleteMessage(t("use_delete.deletesuccec"));
      setDeleteMessageSeverity("success");
      setDeleteMessageOpen(true);
      const key = Array.isArray(queryKey) ? queryKey : [queryKey];
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => {
      setDeleteMessage(error.message);
      setDeleteMessageSeverity("error");
      setDeleteMessageOpen(true);
    },
  });

  const handleDelete = (id) => {
    setItemToDelete(id);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate(itemToDelete);
    setConfirmDialogOpen(false);
  };

  useEffect(() => {
    if (deleteMessageOpen) {
      const timer = setTimeout(() => {
        setDeleteMessageOpen(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [deleteMessageOpen]);

  const DeleteMessageComponent = () => (
    <Box
      sx={{
        display: deleteMessageOpen ? "flex" : "none",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: deleteMessageSeverity === "success" ? "green" : "red",
        color: "#fff",
        px: 2,
        py: 1.25,
        borderRadius: 2,
        position: "fixed",
        top: 90,
        right: 20,
        width: 320,
        maxWidth: "calc(100vw - 32px)",
        zIndex: 1500,
        boxShadow: 4,
      }}
    >
      <Box>{deleteMessage}</Box>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={() => setDeleteMessageOpen(false)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const ConfirmDialog = () => (
    <Dialog
      open={confirmDialogOpen}
      onClose={() => setConfirmDialogOpen(false)}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon color="error" sx={{ fontSize: "5rem" }} />
          <Typography variant="h5" fontWeight={"bold"}>
            {t("use_delete.confiremDeleting")}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>{t("use_delete.deletingMessage")}</DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmDialogOpen(false)}
          color="primary"
        >
          {t("use_delete.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={confirmDelete}
          color="error"
          sx={{ margin: "0px 10px" }}
        >
          {t("use_delete.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { handleDelete, DeleteMessageComponent, ConfirmDialog };
};

export default useDelete;
